<script setup lang="ts">
import {
  Delete,
  Hide,
  DocumentAdd,
  FolderRemove,
} from "@element-plus/icons-vue";
import {
  FormElementBase,
  SectionFormElement,
  FormElementType,
  FormRuleType,
  FormDefinition,
  FormElementEventType,
  SectionAssignables,
  WorkflowDefinition,
  LabelledOption,
} from "~/api";
import { computed, ref, watch } from "vue";
import { sectionRuleTypeDisplayNames } from "~/utils/enumDisplayNames";
import SectionRule from "./SectionRule.vue";
import ElementPropertiesEditor from "./ElementPropertiesEditor.vue";
import ElementEventsEditor from "./ElementEventsEditor.vue";
import ElementRulesEditor from "./ElementRulesEditor.vue";
import { isFormElementBasedCondition } from "~/utils/typeGuards";
import { ElMessage } from "element-plus";

const emit = defineEmits<{
  (e: "delete:formElement", value: FormElementBase): void;
  (e: "archive:formElement", value: FormElementBase): void;
  (e: "unarchive:formElement", value: FormElementBase): void;
  (e: "stopEditing:formElement", value: FormElementBase): void;
  (e: "update:active-tab", value: string): void;
}>();

const props = withDefaults(
  defineProps<{
    canDelete: boolean;
    saveNeeded: boolean;
    sectionAssignables: SectionAssignables;
    workflowDefinition?: WorkflowDefinition;
    activeTab?: string;
    responsesExist?: boolean;
    entityDefinitionsList?: LabelledOption[];
    formDefinitionsList?: LabelledOption[];
    listDefinitionsList?: LabelledOption[];
    eventCodesList?: LabelledOption[];
    securityGroupsList?: LabelledOption[];
    readOnly?: boolean;
  }>(),
  {
    workflowDefinition: undefined,
    activeTab: "properties",
    responsesExist: false,
    entityDefinitionsList: undefined,
    formDefinitionsList: undefined,
    listDefinitionsList: undefined,
    eventCodesList: undefined,
    securityGroupsList: undefined,
    readOnly: false,
  },
);

/** The element we are editing */
const modelValue = defineModel<FormElementBase>({
  required: true,
  /**
   * Called whenever the form element updates.
   * Handles updating the question code in the rules that reference this question.
   */
  set: (value) => {
    // Update all references to this question code
    if (previousQuestionCode.value !== value.questionCode) {
      previousQuestionCode.value = value.questionCode;

      const rulesWithThisQuestionCode = form.value.rules.filter(
        (r) =>
          r.elementGuid === value.guid ||
          r.conditions.some(
            (c) =>
              isFormElementBasedCondition(c) && c.questionGuid === value.guid,
          ),
      );

      if (rulesWithThisQuestionCode.length > 0) {
        rulesWithThisQuestionCode.forEach((r) => {
          if (r.elementGuid === value.guid) {
            // update the question code in the rule
            r.elementQuestionCode = value.questionCode;
          }
          r.conditions.forEach((c) => {
            if (
              isFormElementBasedCondition(c) &&
              c.questionGuid === value.guid
            ) {
              // the condition will only have a question code if it is a form element based condition
              c.questionCode = value.questionCode;
            }
          });
        });
      }
    }
    return value;
  },
});
/** Model ref to the form definition */
const form = defineModel<FormDefinition>("form", { required: true });
/** Save the previous question code so we can compare */
const previousQuestionCode = ref<string>(modelValue.value.questionCode);
/** Current tab we are on */
const currentTabName = ref<string>(props.activeTab ?? "properties");

//--> Can't handle when the desired tab no longer exists
// const currentTabName = ref<string>(
//   localStorage.getItem("elementEditorTab") || "properties",
// );
// const handleTabChange = (tab: string) => {
//   localStorage.setItem("elementEditorTab", tab);
// };
watch(
  () => currentTabName.value,
  (tab) => {
    emit("update:active-tab", tab);
  },
);

const stopEditing = () => {
  emit("stopEditing:formElement", modelValue.value);
};

const validEventTypes = computed<FormElementEventType[]>(() => {
  if (
    modelValue.value.type === FormElementType.NUMBER ||
    modelValue.value.type === FormElementType.MONEY ||
    modelValue.value.type === FormElementType.SIMPLE_TEXT ||
    modelValue.value.type === FormElementType.RADIO_BUTTONS ||
    modelValue.value.type === FormElementType.CHECKBOXES
  ) {
    return [
      FormElementEventType.SUM_TO,
      FormElementEventType.PERCENTAGE_TO,
      FormElementEventType.CUSTOM_EXPRESSION,
    ];
  }

  return [];
});

const rulesForElement = computed(() =>
  form.value.rules
    .filter((rule) => rule.elementGuid === modelValue.value.guid)
    .flatMap((rule) =>
      rule.conditions.map((condition) => ({
        ruleType: rule.ruleType,
        ...condition,
      })),
    ),
);
const dependentRulesForElement = computed(() =>
  form.value.rules.flatMap((rule) =>
    rule.conditions
      .filter(
        (condition) =>
          "questionGuid" in condition &&
          condition.questionGuid === modelValue.value.guid,
      )
      .map((condition) => ({
        ruleType: rule.ruleType,
        affectedQuestionCode: rule.elementQuestionCode,
        ...condition,
      })),
  ),
);

watch(
  () => modelValue.value.questionCode,
  (newValue) => {
    if (newValue) {
      form.value.components.forEach((component) => {
        if (
          component.questionCode === newValue &&
          component.guid !== modelValue.value.guid
        ) {
          ElMessage.warning(
            `Question code '${newValue}' already exists. Please choose a different one.`,
          );
        }
      });
    }
  },
);
</script>

<template id="form-simple-text-option">
  <div class="editorBody">
    <div class="editorHeader">
      <h2>
        {{ modelValue.label }}
      </h2>
      <div class="elementDetails">
        {{ modelValue.type }}
      </div>
      <el-button
        v-if="!saveNeeded"
        class="hideButton"
        type=""
        :icon="Hide"
        size="small"
        circle
        title="Stop editing"
        @click="stopEditing"
      ></el-button>

      <div class="action-row">
        <el-button
          v-if="canDelete"
          class="action-btn"
          type=""
          :icon="Delete"
          size="small"
          :disabled="props.readOnly"
          :title="
            responsesExist
              ? 'This element and any responses will be deleted'
              : props.readOnly
                ? 'Read-only mode'
                : null
          "
          @click="emit('delete:formElement', modelValue)"
          >Delete</el-button
        >

        <el-button
          v-if="canDelete"
          class="action-btn"
          type=""
          :icon="FolderRemove"
          size="small"
          :disabled="props.readOnly"
          :title="
            responsesExist
              ? 'This element will be archived but not deleted'
              : props.readOnly
                ? 'Read-only mode'
                : null
          "
          @click="emit('archive:formElement', modelValue)"
          >Archive</el-button
        >

        <el-button
          v-if="modelValue.isArchived"
          class="action-btn"
          type=""
          :icon="DocumentAdd"
          size="small"
          :disabled="props.readOnly"
          @click="emit('unarchive:formElement', modelValue)"
          >Unarchive</el-button
        >
      </div>
    </div>
    <el-tabs
      v-model="currentTabName"
      class="editorTabs"
      tab-position="top"
      type="border-card"
    >
      <el-tab-pane label="Properties" name="properties">
        <ElementPropertiesEditor
          v-model="modelValue"
          v-model:form="form"
          :workflow-definition="workflowDefinition"
          :entity-definitions-list="props.entityDefinitionsList"
          :form-definitions-list="props.formDefinitionsList"
          :list-definitions-list="props.listDefinitionsList"
          :event-codes-list="props.eventCodesList"
          :security-groups-list="props.securityGroupsList"
          :read-only="props.readOnly"
        />
        <div class="elementGuid">{{ modelValue.guid }}</div>
      </el-tab-pane>

      <el-tab-pane
        v-if="validEventTypes.length > 0"
        :label="`Computed${modelValue.events.length > 0 ? `(${modelValue.events.length})` : ''}`"
        name="events"
      >
        <ElementEventsEditor
          v-model="modelValue.events"
          v-model:form="form"
          :valid-event-types="validEventTypes"
          :form-element="modelValue"
          :read-only="props.readOnly"
        />
      </el-tab-pane>
      <el-tab-pane
        :label="`Rules${
          rulesForElement.length > 0 || dependentRulesForElement.length > 0
            ? ` (${rulesForElement.length},${dependentRulesForElement.length})`
            : ''
        }`"
        name="rules"
      >
        <ElementRulesEditor
          :rules="rulesForElement"
          :dependent-rules="dependentRulesForElement"
          :read-only="props.readOnly"
        />
      </el-tab-pane>
      <template v-if="modelValue.type === FormElementType.SECTION">
        <!--Remove the filtering to include approvals and reviews tabs-->
        <el-tab-pane
          v-for="ruleType of Object.values(FormRuleType).filter(
            (rt) => !['RequiresReview', 'RequiresApproval'].includes(rt),
          )"
          :key="ruleType"
          :label="sectionRuleTypeDisplayNames[ruleType]"
          :name="sectionRuleTypeDisplayNames[ruleType]"
        >
          <SectionRule
            v-model:form="form"
            :rule-type="ruleType"
            :assignables="sectionAssignables"
            :section-element="modelValue as SectionFormElement"
            :read-only="props.readOnly"
          />
        </el-tab-pane>
      </template>
    </el-tabs>
  </div>
</template>

<style lang="less">
.editorBody {
  max-height: 55vh;
  padding: 10px;
  .editorTabs {
    display: flex;
    overflow: auto;
    .el-tabs__content {
      overflow: auto;
      max-height: 100%;
    }
  }

  .editorHeader {
    display: grid;
    grid-template-columns: auto auto 1fr auto;
    align-items: center;
    gap: 30px;
    padding-bottom: 10px;
    h2 {
      margin: 0;
      font-size: 20px;
    }

    .elementDetails {
      color: #757575;
      font-size: 14px;
    }

    .deleteButton {
      margin-left: auto;
    }
  }

  .elementGuid {
    font-size: 14px;
    margin: 0.5em 0 -1em auto;
    opacity: 0.3;
    width: fit-content;
  }
}
</style>
